import { List, Tooltip } from 'antd'

import TotalCard from 'atom/cards/TotalCard'
import { ListCardWrapper } from 'atom/list'

import { Financial, V3Transaction } from 'models/v3-transaction'
import { formatDurationWithTwoDigits } from 'helpers/duration'
import { formatPrice, formatSecondsToMinutes } from 'helpers/number-format'
import { useFinancialTranslation } from 'hooks/translation/useFinancialTranslation'

interface FinancialProps {
  financial: Financial
  transaction: V3Transaction
}

const TransactionPanelTotal: React.FC<FinancialProps> = ({ financial, transaction }) => {
  const {
    taxText,
    subTotalText,
    totalText,
    localTaxText,
    loiteringFeeText,
    loiteringDurationText,
    chargingFeeText,
    flatFeeText,
    guestFeeText,
  } = useFinancialTranslation()

  const {
    tax,
    subtotal,
    total,
    currency,
    localTax,
    loiteringFeeCents,
    loiteringDuration,
    chargingFeeCents,
    flatFeeCents,
    guestFeeCents,
  } = financial

  const parseNumberish = (value?: string | number): number | undefined => {
    if (value === null || value === undefined || value === '') return undefined
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : undefined
  }

  const resolvedTieredPercent = parseNumberish(transaction.tieredPercent)
  const resolvedCurtailmentAdjustmentCents = parseNumberish(transaction.curtailmentAdjustmentCents)

  const curtailmentAdjustmentPercent =
    typeof resolvedTieredPercent === 'number' ? (1 - resolvedTieredPercent) * 100 : undefined

  const formattedCurtailmentAdjustmentPercent =
    typeof curtailmentAdjustmentPercent === 'number'
      ? (() => {
          const fixed = curtailmentAdjustmentPercent.toFixed(2)
          return fixed.endsWith('.00') ? fixed.replace('.00', '') : fixed
        })()
      : undefined

  return (
    <ListCardWrapper>
      <TotalCard>
        <List.Item
          actions={[<span className="heading-06-regular-td">{formatPrice(chargingFeeCents / 100, currency)}</span>]}
        >
          <List.Item.Meta title={chargingFeeText} />
        </List.Item>
        {!!(resolvedCurtailmentAdjustmentCents && resolvedCurtailmentAdjustmentCents > 0) && (
          <List.Item
            actions={[<span className="heading-06-regular-td">{formatPrice(resolvedCurtailmentAdjustmentCents / 100, currency)}</span>]}
          >
            <List.Item.Meta
              title={
                <Tooltip
                  getPopupContainer={() => document.body}
                  placement="topLeft"
                  overlayStyle={{ maxWidth: 'none' }}
                  overlayInnerStyle={{ whiteSpace: 'nowrap', maxWidth: 'none' }}
                  title={`Charge Amount * ${formattedCurtailmentAdjustmentPercent ?? '-'}% = ${formatPrice(resolvedCurtailmentAdjustmentCents / 100)}`}
                >
                  <span>Curtailment Adjustment</span>
                </Tooltip>
              }
            />
          </List.Item>
        )}
        {!!(guestFeeCents && guestFeeCents > 0) && (
          <List.Item
            actions={[<span className="heading-06-regular-td">{formatPrice(guestFeeCents / 100, currency)}</span>]}
          >
            <List.Item.Meta title={guestFeeText} />
          </List.Item>
        )}
        {!!(flatFeeCents && flatFeeCents > 0) && (
          <List.Item
            actions={[<span className="heading-06-regular-td">{formatPrice(flatFeeCents / 100, currency)}</span>]}
          >
            <List.Item.Meta title={flatFeeText} />
          </List.Item>
        )}
        {!!(loiteringDuration && loiteringDuration > 0) && (
          <List.Item
            actions={[
              <span className="heading-06-regular-td">
                {loiteringDuration >= 3600
                  ? formatDurationWithTwoDigits(loiteringDuration)
                  : formatSecondsToMinutes(loiteringDuration)}
              </span>,
            ]}
          >
            <List.Item.Meta title={loiteringDurationText} />
          </List.Item>
        )}
        {!!(loiteringFeeCents && loiteringFeeCents > 0) && (
          <List.Item
            actions={[<span className="heading-06-regular-td">{formatPrice(loiteringFeeCents / 100, currency)}</span>]}
          >
            <List.Item.Meta title={loiteringFeeText} />
          </List.Item>
        )}
        <List.Item
          className="showBorderTop"
          actions={[<span className="heading-06-regular-td">{formatPrice(subtotal / 100, currency)}</span>]}
        >
          <List.Item.Meta title={subTotalText} />
        </List.Item>
        {!!(localTax && localTax > 0) && (
          <List.Item actions={[<span className="heading-06-regular-td">{formatPrice(localTax / 100, currency)}</span>]}>
            <List.Item.Meta title={localTaxText} />
          </List.Item>
        )}
        {financial.taxDetails && financial.taxDetails.length > 0 && (
          financial.taxDetails.map((taxDetail, index) => (
            <List.Item
              key={index}
              actions={[<span className="heading-06-regular-td">{formatPrice(taxDetail.taxAmount / 100, currency)}</span>]}
            >
              <List.Item.Meta 
                title={
                  <div>
                    <div>{taxDetail.taxDisplayName || taxText}</div>
                    {taxDetail.taxDisplayNote && (
                      <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                        {taxDetail.taxDisplayNote}
                      </div>
                    )}
                  </div>
                }
              />
            </List.Item>
          ))
        )}
        <List.Item actions={[<span className="heading-06-regular-td">{formatPrice(tax / 100, currency)}</span>]}>
          <List.Item.Meta title={taxText} />
        </List.Item>
        <List.Item
          className="showBorderTop"
          actions={[<span className="paragraph-03-bold">{formatPrice(total / 100, currency)}</span>]}
        >
          <List.Item.Meta title={totalText} />
        </List.Item>
      </TotalCard>
    </ListCardWrapper>
  )
}

export default TransactionPanelTotal

